// Сторінка реферальної програми "3 за 3":
// Запроси 3 друзів → отримай 3 дні Premium (по 1 на кожного).
// Друг отримує 1 день Premium при заході через твоє посилання.
//
// Ключовий UX-елемент: велика прогрес-плитка + кнопка "Поділитись у Telegram"
// яка через Telegram.WebApp.openTelegramLink() одразу відкриває share-меню.
import { useState } from 'react';
import { api } from '../lib/api';
import { useFetch } from '../lib/useFetch';
import { Loader } from '../components/Loader';
import { ErrorView } from '../components/ErrorView';
import { PageHeader } from '../components/PageHeader';
import { haptic } from '../lib/telegram';
import { IconTile, TabGlyph } from '../components/AppIcons';

export function Referral() {
  const { data, loading, error, reload } = useFetch(() => api.referral(), []);
  const [copied, setCopied] = useState(false);

  if (loading) return <Loader />;
  if (error) return <ErrorView error={error} onRetry={reload} />;
  if (!data) return null;

  const link = data.refLink;
  const shareText =
    `🚦 Я готуюсь до іспиту в ТСЦ через @pdr_testy_bot — реальні тести 2026 року, ` +
    `формат точно як на іспиті.\n\n` +
    `Зайди через моє посилання — обоє отримаємо бонус Premium 🎁`;

  function shareTg() {
    if (!link) return;
    haptic.medium();
    const url = `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(shareText)}`;
    const tg = (window as { Telegram?: { WebApp?: { openTelegramLink?: (u: string) => void } } })
      .Telegram?.WebApp;
    if (tg?.openTelegramLink) tg.openTelegramLink(url);
    else window.open(url, '_blank');
  }

  function copyLink() {
    if (!link) return;
    navigator.clipboard.writeText(link).then(() => {
      setCopied(true);
      haptic.light();
      setTimeout(() => setCopied(false), 1800);
    });
  }

  // Прогрес у поточній трійці: 0/3, 1/3, 2/3
  const progressPct = (data.currentTierProgress / data.threshold) * 100;

  return (
    <>
      <PageHeader title="Запроси друзів" subtitle="Готуйтесь до іспиту разом" backTo="/" />
      <div className="space-y-4 p-4">
        {/* Конкурс — показуємо лише поки активний (сервер віддає contest=null поза вікном) */}
        {data.contest && (
          <section className="rounded-xl border border-yellow-500/50 bg-yellow-500/10 p-4">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-yellow-500">
              🏆 Конкурс до {data.contest.endLabel}
            </div>
            <h2 className="mt-1 text-base font-bold leading-tight">
              Приведи найбільше друзів — отримай {data.contest.prizeTop}
            </h2>
            <p className="mt-1.5 text-xs leading-relaxed text-tg-hint">
              + розіграш {data.contest.prizeDraw} серед усіх, хто привів хоча б одного (кожен друг
              = білет). Друг рахується, коли пройде свій перший тест. Підсумки{' '}
              {data.contest.resultsLabel}.
            </p>
            <div className="mt-3 grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg bg-tg-bg/60 py-2">
                <div className="text-lg font-bold text-tg-success">{data.contest.active}</div>
                <div className="text-[10px] text-tg-hint">зараховано</div>
              </div>
              <div className="rounded-lg bg-tg-bg/60 py-2">
                <div className="text-lg font-bold">{data.contest.invited}</div>
                <div className="text-[10px] text-tg-hint">запрошено</div>
              </div>
              <div className="rounded-lg bg-tg-bg/60 py-2">
                <div className="text-lg font-bold text-yellow-500">
                  {data.contest.rank ? `#${data.contest.rank}` : '—'}
                </div>
                <div className="text-[10px] text-tg-hint">
                  {data.contest.participants > 0 ? `з ${data.contest.participants} учасн.` : 'місце'}
                </div>
              </div>
            </div>
            {data.contest.active === 0 && (
              <p className="mt-2 text-[11px] text-yellow-500">
                Кинь посилання нижче в чат автошколи, класу чи групи — перший друг, що пройде тест,
                вводить тебе в розіграш.
              </p>
            )}
          </section>
        )}

        {/* Hero — пояснюємо механіку */}
        <section className="relative overflow-hidden rounded-xl bg-gradient-to-br from-yellow-500/15 via-tg-secondary-bg to-tg-secondary-bg p-4">
          <div className="absolute -right-3 -top-3 opacity-10">
            <TabGlyph kind="gift" className="h-20 w-20" />
          </div>
          <div className="relative">
            <div className="mb-1.5 inline-flex items-center gap-1.5 rounded-full bg-yellow-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-yellow-500">
              <TabGlyph kind="crown" className="h-3 w-3" />
              <span>Premium у подарунок</span>
            </div>
            <h2 className="text-base font-bold leading-tight">
              Запроси {data.threshold} друзів — отримай {data.bonusDays} дні Premium
            </h2>
            <p className="mt-1.5 text-xs text-tg-hint leading-relaxed">
              Кожен друг, що зайде через твоє посилання, отримає {data.inviteeBonusDays} день Premium.
              За кожні {data.threshold} активних — тобі ще +{data.bonusDays} дні підписки.
              Без обмежень — запрошуй скільки хочеш.
            </p>
            <p className="mt-1.5 text-[11px] font-medium text-yellow-500">
              Друг рахується, коли пройде свій перший тест — просто перейти за посиланням
              недостатньо.
            </p>
          </div>
        </section>

        {/* Прогрес до наступної нагороди */}
        <section className="card space-y-3">
          <div className="flex items-baseline justify-between">
            <h3 className="text-sm font-semibold">До наступної нагороди</h3>
            <span className="text-xs text-tg-hint">
              {data.currentTierProgress} / {data.threshold}
            </span>
          </div>
          <div className="h-3 overflow-hidden rounded-full bg-tg-bg">
            <div
              className="h-full bg-gradient-to-r from-tg-success to-tg-button transition-all"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <p className="text-xs text-tg-hint">
            {data.remainingToNextBonus === data.threshold
              ? `Запроси ${data.threshold} друзів і отримай +${data.bonusDays} дні Premium`
              : `Залишилось запросити ${data.remainingToNextBonus} ${plurFriends(data.remainingToNextBonus)} → +${data.bonusDays} дні Premium`}
          </p>
        </section>

        {/* Велика кнопка Поділитись */}
        <section className="space-y-2">
          {link ? (
            <>
              <button
                onClick={shareTg}
                className="w-full rounded-xl bg-tg-button py-4 text-base font-bold text-tg-button-text"
              >
                Поділитись у Telegram
              </button>
              <button
                onClick={copyLink}
                className="w-full rounded-xl bg-tg-secondary-bg py-3 text-sm font-medium text-tg-text"
              >
                {copied ? '✓ Скопійовано' : 'Скопіювати посилання'}
              </button>
              <div className="break-all rounded-lg bg-tg-bg/40 p-2 text-center text-[11px] text-tg-hint">
                {link}
              </div>
            </>
          ) : (
            <div className="card text-center text-sm text-tg-hint">
              Посилання недоступне — переконайся що зайшов через Telegram.
            </div>
          )}
        </section>

        {/* Підсумкова статистика */}
        <section className="grid grid-cols-3 gap-2">
          <SummaryTile
            icon="friends"
            label="Активних"
            value={data.refereeCount.toString()}
            sub={`з ${data.refereesTotal} запрошених`}
          />
          <SummaryTile
            icon="gift"
            label="Отримано"
            value={`+${data.totalDaysEarned}`}
            sub={`${plurDays(data.totalDaysEarned)} Premium`}
            accent="success"
          />
          <SummaryTile
            icon="medal"
            label="Нагород"
            value={data.bonusesGranted.toString()}
            sub={`по ${data.bonusDays} дні`}
            accent="warning"
          />
        </section>

        {/* Список запрошених */}
        {data.referees.length > 0 && (
          <section className="card space-y-2">
            <h3 className="text-sm font-semibold">Хто прийшов через тебе</h3>
            <div className="space-y-1.5">
              {data.referees.map((r, i) => (
                <RefereeRow key={i} r={r} />
              ))}
            </div>
            {data.refereeCount > data.referees.length && (
              <p className="text-center text-[11px] text-tg-hint">
                Показано останніх {data.referees.length} з {data.refereeCount}
              </p>
            )}
          </section>
        )}

        {/* Як це працює */}
        <section className="card space-y-3">
          <h3 className="text-sm font-semibold">Як це працює</h3>
          <Step n={1} title="Поділись посиланням" desc="Кинь у чат друзям, у групу автошколи або в Stories." />
          <Step n={2} title="Друг заходить і проходить тест" desc={`Він тицяє посилання → отримує ${data.inviteeBonusDays} день Premium у подарунок. Щойно пройде свій перший тест — стає «активним».`} />
          <Step n={3} title="Ти отримуєш бонус" desc={`За кожні ${data.threshold} АКТИВНИХ запрошених — +${data.bonusDays} дні Premium тобі. Фейкові кліки без тесту не рахуються.`} last />
        </section>
      </div>
    </>
  );
}

// ===== Subcomponents =====

function SummaryTile({
  icon,
  label,
  value,
  sub,
  accent,
}: {
  icon: Parameters<typeof IconTile>[0]['kind'];
  label: string;
  value: string;
  sub: string;
  accent?: 'success' | 'warning';
}) {
  const valueCls =
    accent === 'success'
      ? 'text-tg-success'
      : accent === 'warning'
        ? 'text-yellow-500'
        : 'text-tg-text';
  return (
    <div className="rounded-lg bg-tg-secondary-bg p-2.5">
      <IconTile kind={icon} className="h-7 w-7 rounded-lg" glyphClass="h-4 w-4" />
      <div className="mt-1 text-[10px] uppercase tracking-wide text-tg-hint">{label}</div>
      <div className={`mt-0.5 text-lg font-bold leading-tight ${valueCls}`}>{value}</div>
      <div className="text-[10px] text-tg-hint">{sub}</div>
    </div>
  );
}

function Step({
  n,
  title,
  desc,
  last,
}: {
  n: number;
  title: string;
  desc: string;
  last?: boolean;
}) {
  return (
    <div className={`flex gap-3 ${last ? '' : 'border-b border-tg-bg/40 pb-3'}`}>
      <div className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-tg-button text-xs font-bold text-tg-button-text">
        {n}
      </div>
      <div className="min-w-0">
        <div className="text-sm font-medium leading-tight">{title}</div>
        <div className="mt-0.5 text-xs text-tg-hint leading-relaxed">{desc}</div>
      </div>
    </div>
  );
}

function RefereeRow({
  r,
}: {
  r: {
    firstName: string | null;
    lastNameInitial: string | null;
    joinedAt: string;
    isPremium: boolean;
    tookTest: boolean;
  };
}) {
  const name = [r.firstName, r.lastNameInitial].filter(Boolean).join(' ') || 'Анонім';
  const date = new Date(r.joinedAt);
  return (
    <div className="flex items-center justify-between rounded-lg bg-tg-bg/40 px-2.5 py-2 text-sm">
      <div className="flex items-center gap-2">
        <span className="grid h-7 w-7 place-items-center rounded-full bg-tg-secondary-bg text-tg-hint">
          <TabGlyph kind="profile" className="h-4 w-4" />
        </span>
        <div>
          <div className="font-medium">{name}</div>
          <div className="text-[10px] text-tg-hint">
            {date.toLocaleDateString('uk-UA')} · {date.toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        {r.tookTest ? (
          <span className="rounded bg-tg-success/15 px-1.5 py-0.5 text-[10px] font-semibold text-tg-success">
            ✓ активний
          </span>
        ) : (
          <span className="rounded bg-tg-bg px-1.5 py-0.5 text-[10px] text-tg-hint">
            ще без тесту
          </span>
        )}
        {r.isPremium && (
          <span className="inline-flex items-center gap-1 rounded bg-yellow-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-yellow-500">
            <TabGlyph kind="crown" className="h-3 w-3" /> Premium
          </span>
        )}
      </div>
    </div>
  );
}

// ===== helpers =====

function plurFriends(n: number): string {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return 'друга';
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return 'друзів';
  return 'друзів';
}

function plurDays(n: number): string {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return 'день';
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return 'дні';
  return 'днів';
}
